import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  CommunicationLog,
  CommunicationLogChannelFilter,
  CommunicationLogPage,
} from '@guilleumes/types';
import { messagingKeys } from './useMessagingTask';

// ═══════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════

export type CommunicationLogOwner = 'reservation' | 'client';

export interface CommunicationLogFilters {
  channel?: CommunicationLogChannelFilter;
}

/** Claus jeràrquiques (plans/whatsapp-capture.md A4.1). */
export const communicationLogKeys = {
  all: ['communication-logs'] as const,
  owner: (owner: CommunicationLogOwner, id: string) =>
    [...communicationLogKeys.all, owner, id] as const,
  list: (owner: CommunicationLogOwner, id: string, filters: CommunicationLogFilters) =>
    [...communicationLogKeys.owner(owner, id), filters] as const,
};

/** Mida de pàgina: prou per a una estada normal, sense renderitzar milers de files. */
export const COMMUNICATION_LOGS_PAGE_SIZE = 100;

// ═══════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════

function useCommunicationLogsPages(
  owner: CommunicationLogOwner,
  id: string | undefined,
  filters: CommunicationLogFilters,
) {
  return useInfiniteQuery({
    queryKey: communicationLogKeys.list(owner, id ?? '', filters),
    queryFn: async ({ pageParam }) => {
      const res = await api.get<CommunicationLogPage>('/messaging/logs', {
        params: {
          [owner === 'reservation' ? 'reservationId' : 'clientId']: id,
          channel: filters.channel,
          take: COMMUNICATION_LOGS_PAGE_SIZE,
          cursor: pageParam,
        },
      });
      return res.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!id,
  });
}

/** Historial paginat d'un client (més recent primer). */
export function useCommunicationLogs(clientId?: string, filters: CommunicationLogFilters = {}) {
  return useCommunicationLogsPages('client', clientId, filters);
}

/** Historial paginat d'una reserva (més recent primer). */
export function useCommunicationLogsByReservation(
  reservationId?: string,
  filters: CommunicationLogFilters = {},
) {
  return useCommunicationLogsPages('reservation', reservationId, filters);
}

// ═══════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════

interface CreateLogParams {
  rawContent: string;
  clientId?: string;
  reservationId?: string;
  source?: 'WHATSAPP_PASTE' | 'EMAIL' | 'OTHER';
}

export function useCreateCommunicationLog() {
  const queryClient = useQueryClient();

  return useMutation<CommunicationLog, Error, CreateLogParams>({
    mutationFn: async (data) => {
      const res = await api.post('/messaging/logs', data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      // Com abans: la resta de vistes de missatgeria també es refresquen.
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
      if (variables.clientId) {
        queryClient.invalidateQueries({
          queryKey: communicationLogKeys.owner('client', variables.clientId),
        });
      }
      if (variables.reservationId) {
        queryClient.invalidateQueries({
          queryKey: communicationLogKeys.owner('reservation', variables.reservationId),
        });
      }
    },
  });
}
